import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Card, HelperText, TextInput } from 'react-native-paper';
import { isEmail, isRequired, sameAs, usPhoneNumber, } from '@luminate/luminate-ts-sdk';
import { EmailInput, PasswordInput, PhoneTypePicker, StatePicker, ValidatedInput, } from './common';
export const AccountProfile = (props) => {
    const styles = StyleSheet.create({
        inputStyle: {
            marginVertical: 5,
        },
    });
    const [reValidateConfirmEmail, setRevalidateConfirmEmail] = useState(false);
    const [confirmNewEmailErrors, setConfirmNewEmailErrors] = useState([]);
    const [isConfirmNewEmailValid, setIsConfirmNewEmailValid] = useState(false);
    const [emailPasswordErrors, setEmailPasswordErrors] = useState([]);
    const [isEmailPasswordValid, setIsEmailPasswordValid] = useState(false);
    const onEmailPasswordValidated = (errors) => {
        setEmailPasswordErrors(errors);
        setIsEmailPasswordValid(errors.length === 0);
    };
    const [newEmailErrors, setNewEmailErrors] = useState([]);
    const [isNewEmailValid, setIsNewEmailValid] = useState(props.email !== undefined && isEmail(props.email) === undefined);
    const onNewEmailValidated = (errors) => {
        setIsNewEmailValid(errors.length === 0);
        setNewEmailErrors(errors);
        setRevalidateConfirmEmail(true);
    };
    const onConfirmNewEmailValidated = (errors) => {
        setConfirmNewEmailErrors(errors);
        setIsConfirmNewEmailValid(errors.length === 0);
        setRevalidateConfirmEmail(false);
    };
    const [phoneErrors, setPhoneErrors] = useState([]);
    const [isPhoneValid, setIsPhoneValid] = useState(props.phone !== undefined);
    const onPhoneValidated = (errors) => {
        setPhoneErrors(errors);
        setIsPhoneValid(errors.length === 0);
    };
    const [address1Errors, setAddress1Errors] = useState([]);
    const [isAddress1Valid, setIsAddress1Valid] = useState(props.address1 !== undefined);
    const onAddress1Validated = (errors) => {
        setAddress1Errors(errors);
        setIsAddress1Valid(errors.length === 0);
    };
    const [cityErrors, setCityErrors] = useState([]);
    const [isCityValid, setIsCityValid] = useState(props.city !== undefined);
    const onCityValidated = (errors) => {
        setCityErrors(errors);
        setIsCityValid(errors.length === 0);
    };
    const [zipErrors, setZipErrors] = useState([]);
    const [isZipValid, setIsZipValid] = useState(props.zip !== undefined);
    const onZipValidated = (errors) => {
        setZipErrors(errors);
        setIsZipValid(errors.length === 0);
    };
    const isFormValid = () => {
        return (isPhoneValid &&
            isAddress1Valid &&
            isCityValid &&
            isZipValid &&
            ((props.emailEditable &&
                isEmailPasswordValid &&
                isNewEmailValid &&
                isConfirmNewEmailValid) ||
                !props.emailEditable));
    };
    const emailsMustMatch = (confirmEmail, message = 'Email addresses must match') => {
        return sameAs({ first: props.newEmail, second: confirmEmail }, message);
    };
    useEffect(() => {
        if (props.onValidationChanged) {
            props.onValidationChanged(isFormValid());
        }
        return () => {
            /* cancel operation */
        };
    }, []);
    useEffect(() => {
        if (props.onValidationChanged) {
            props.onValidationChanged(isFormValid());
        }
        return () => {
            /* cancel operation */
        };
    }, [
        isPhoneValid,
        isAddress1Valid,
        isCityValid,
        isZipValid,
        props.emailEditable,
        isNewEmailValid,
        isConfirmNewEmailValid,
        isEmailPasswordValid,
    ]);
    const onToggleNewPasswordEdit = (enabled) => {
        if (props.onToggleEditEmail) {
            props.onToggleEditEmail(enabled);
        }
        setNewEmailErrors([]);
        setIsNewEmailValid(false);
        setConfirmNewEmailErrors([]);
        setIsConfirmNewEmailValid(false);
        setEmailPasswordErrors([]);
        setIsEmailPasswordValid(false);
        if (!enabled) {
            if (props.onNewEmailPasswordChanged) {
                props.onNewEmailPasswordChanged(undefined);
            }
            if (props.onNewEmailChanged) {
                props.onNewEmailChanged(undefined);
            }
            if (props.onConfirmNewEmailChanged) {
                props.onConfirmNewEmailChanged(undefined);
            }
        }
    };
    return (_jsx(_Fragment, { children: _jsxs(View, { style: [props.style], children: [_jsx(TextInput, { value: props?.firstName, label: 'First Name', mode: 'outlined', style: styles.inputStyle, disabled: true }), _jsx(TextInput, { value: props?.lastName, label: 'Last Name', mode: 'outlined', style: styles.inputStyle, disabled: true }), !props.emailEditable ? (_jsx(TextInput, { label: 'Email', mode: 'outlined', numberOfLines: 1, style: styles.inputStyle, editable: false, right: props.enableEmailUpdate && (_jsx(TextInput.Icon, { icon: 'pencil', onPress: () => onToggleNewPasswordEdit(!props.emailEditable) })), value: props.email })) : (_jsx(_Fragment, {})), props.emailEditable && (_jsxs(Card, { children: [_jsx(Card.Title, { title: 'Change E-mail Address' }), _jsxs(Card.Content, { children: [_jsx(EmailInput, { label: 'New Email Address', mode: 'outlined', style: styles.inputStyle, right: _jsx(TextInput.Icon, { icon: 'pencil-off', onPress: () => onToggleNewPasswordEdit(!props.emailEditable) }), value: props.newEmail, onChangeText: props?.onNewEmailChanged, errors: newEmailErrors, onValidate: onNewEmailValidated }), _jsx(ValidatedInput, { label: 'Re-type New Email Address', mode: 'outlined', style: styles.inputStyle, keyboardType: 'email-address', autoCorrect: false, autoCapitalize: 'none', errors: confirmNewEmailErrors, value: props.confirmNewEmail, rules: [emailsMustMatch], reValidate: reValidateConfirmEmail, onChangeText: props?.onConfirmNewEmailChanged, onValidate: onConfirmNewEmailValidated }, 'confirm-new-email'), _jsx(PasswordInput, { label: 'Password', mode: 'outlined', style: styles.inputStyle, value: props.newEmailPassword, onChangeText: props?.onNewEmailPasswordChanged, rules: [isRequired], errors: emailPasswordErrors, onValidate: onEmailPasswordValidated }), _jsx(HelperText, { type: 'info', children: "You must specify your current password in order to change your email address." })] })] })), _jsx(ValidatedInput, { label: 'Phone Number', mode: 'outlined', style: styles.inputStyle, keyboardType: 'phone-pad', value: props.phone, onChangeText: props.onPhoneChanged, rules: [usPhoneNumber], errors: phoneErrors, onValidate: onPhoneValidated }), _jsx(PhoneTypePicker, { value: props.phoneType, onValueChange: props.onPhoneTypeChanged, style: styles.inputStyle }), _jsx(ValidatedInput, { label: 'Street Address', mode: 'outlined', style: styles.inputStyle, value: props?.address1, onChangeText: props.onAddress1Changed, rules: [isRequired], errors: address1Errors, onValidate: onAddress1Validated }), _jsx(TextInput, { label: 'Address (Continued)', mode: 'outlined', style: styles.inputStyle, value: props?.address2, onChangeText: props?.onAddress2Changed }), _jsx(ValidatedInput, { label: 'City', mode: 'outlined', style: styles.inputStyle, value: props?.city, onChangeText: props?.onCityChanged, rules: [isRequired], errors: cityErrors, onValidate: onCityValidated }), _jsx(StatePicker, { value: props.state, onValueChange: props.onStateChanged, style: styles.inputStyle, enablePlaceHolder: false }), _jsx(ValidatedInput, { label: 'Zip', mode: 'outlined', style: styles.inputStyle, value: props?.zip, onChangeText: props?.onZipChanged, rules: [isRequired], errors: zipErrors, onValidate: onZipValidated, keyboardType: 'decimal-pad' })] }) }));
};
